import React, { useEffect, useState, useRef } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import toast from 'react-hot-toast'
import AdminLayout from '../../layouts/AdminLayout'
import { adminMessageService } from '@services/api/adminMessages'
import { useAuthStore } from '@store'
import { usePermissions } from '@/hooks/usePermissions'

const AdminMessagesPage = () => {
  const location = useLocation()
  const navigate = useNavigate()
  const { user } = useAuthStore()
  const { isAdmin: canModerate } = usePermissions()
  const [conversations, setConversations] = useState([])
  const [activeConversation, setActiveConversation] = useState(null)
  const [messages, setMessages] = useState([])
  const [loading, setLoading] = useState(true)
  const [loadingMessages, setLoadingMessages] = useState(false)
  const [searchTerm, setSearchTerm] = useState('')
  const [newMessageText, setNewMessageText] = useState('')
  const [showNewMessageModal, setShowNewMessageModal] = useState(false)
  const [selectedClient, setSelectedClient] = useState(null)
  const [postContext, setPostContext] = useState(null)
  const [sending, setSending] = useState(false)
  const [showContactDetail, setShowContactDetail] = useState(false)
  const [menuOpenId, setMenuOpenId] = useState(null)
  const [editingId, setEditingId] = useState(null)
  const [editText, setEditText] = useState('')
  const [partnerTyping, setPartnerTyping] = useState(false)
  const lastTypingSentRef = useRef(0)
  const [clientsList, setClientsList] = useState([])
  const [searchClientQuery, setSearchClientQuery] = useState('')
  const [loadingClients, setLoadingClients] = useState(false)
  const messagesEndRef = useRef(null)
  const messagesIntervalRef = useRef(null)
  const conversationsIntervalRef = useRef(null)

  // Fonction pour scroller vers le bas
  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' })
  }

  const getUserId = () => {
    if (user?.users_id) return user.users_id
    if (user?.client_id) return user.client_id
    if (user?.id) return user.id
    try {
      const stored = localStorage.getItem('kudhi_user') || localStorage.getItem('admin_user') || localStorage.getItem('user')
      if (stored) {
        const d = JSON.parse(stored)
        return d.users_id || d.client_id || d.id || d.user_id || null
      }
    } catch {}
    return null
  }

  const loadConversations = async (silent = false) => {
    try {
      if (!silent) setLoading(true)
      const response = await adminMessageService.getAdminConversations()
      if (response.success) {
        setConversations(response.data)
      }
    } catch (error) {
      console.error('Erreur chargement conversations:', error)
      if (!silent) toast.error('Erreur lors du chargement des conversations')
    } finally {
      if (!silent) setLoading(false)
    }
  }

  // Charger les messages d'une conversation
  const loadMessages = async (conversationId, silent = false) => {
    const userId = getUserId()
    if (!userId || !conversationId) return
    
    try {
      if (!silent) setLoadingMessages(true)
      const response = await adminMessageService.getMessages(conversationId, userId)
      if (response.success) {
        const hadMessages = messages.length > 0
        const oldLength = messages.length
        const newLength = response.data.length

        setPartnerTyping(!!response.partner_typing)
        setMessages(response.data)
        
        // Scroller seulement si nouveaux messages
        if (newLength > oldLength) {
          setTimeout(scrollToBottom, 100)
        } else if (!hadMessages) {
          setTimeout(scrollToBottom, 100)
        }
      }
    } catch (error) {
      console.error('Erreur chargement messages:', error)
    } finally {
      if (!silent) setLoadingMessages(false)
    }
  }

  const loadClientsList = async (search = '') => {
    const userId = getUserId()
    if (!userId) {
      return
    }
    
    try {
      setLoadingClients(true)
      const response = await adminMessageService.getClientsList(userId, search)
      if (response.success) {
        setClientsList(response.data)
      }
    } catch (error) {
      console.error('Erreur chargement liste clients:', error)
    } finally {
      setLoadingClients(false)
    }
  }

  // Créer ou ouvrir une conversation avec un client
  const createOrOpenConversation = async (clientId, clientInfo) => {
    const userId = getUserId()
    if (!userId) return
    
    try {
      // Vérifier si une conversation existe déjà
      const existingConv = conversations.find(
        conv => conv.contact_id === clientId
      )
      
      if (existingConv) {
        setActiveConversation(existingConv)
        await loadMessages(existingConv.id)
        setShowNewMessageModal(false)
      } else {
        // Créer une nouvelle conversation
        const response = await adminMessageService.createAdminConversation(userId, clientId)
        if (response.success) {
          const newConv = {
            id: response.data.conversation_id,
            contact_id: clientId,
            contact_full_name: clientInfo.name,
            contact_avatar: clientInfo.avatar,
            contact_phone: clientInfo.phone,
            contact_email: clientInfo.email,
            unread_count: 0,
            last_message: '',
            last_message_time: new Date().toISOString()
          }
          setConversations([newConv, ...conversations])
          setActiveConversation(newConv)
          setMessages([])
          setShowNewMessageModal(false)
        }
      }
    } catch (error) {
      console.error('Erreur création conversation:', error)
      toast.error('Erreur lors de la création de la conversation')
    }
  }

  useEffect(() => {
    loadConversations()
    
    // Si on vient de la page de vérification avec un article
    if (location.state?.postDetails) {
      setPostContext(location.state.postDetails)
      setSelectedClient({
        id: location.state.clientId,
        name: location.state.clientName
      })
      setShowNewMessageModal(true)
      
      // Créer un message pré-rempli avec les détails de l'article
      const postDetails = location.state.postDetails
      const messageTemplate = `Bonjour,\n\nConcernant votre publication "${postDetails.title}" (${postDetails.type})\n\nDétails de la publication :\n- Catégorie : ${postDetails.category}\n${postDetails.price ? `- Prix : ${parseInt(postDetails.price).toLocaleString()} FCFA\n` : ''}${postDetails.location ? `- Localisation : ${postDetails.location}\n` : ''}\n`
      setNewMessageText(messageTemplate)
    }
    
    // Rafraîchir les conversations toutes les 5 secondes
    conversationsIntervalRef.current = setInterval(() => {
      loadConversations(true)
    }, 5000)
    
    return () => {
      if (conversationsIntervalRef.current) {
        clearInterval(conversationsIntervalRef.current)
      }
      if (messagesIntervalRef.current) {
        clearInterval(messagesIntervalRef.current)
      }
    }
  }, [user])

  // Effect pour rafraîchir les messages de la conversation active
  useEffect(() => {
    // Nettoyer l'ancien interval
    if (messagesIntervalRef.current) {
      clearInterval(messagesIntervalRef.current)
    }
    
    // Si une conversation est active, rafraîchir ses messages toutes les 3 secondes
    if (activeConversation?.id) {
      messagesIntervalRef.current = setInterval(() => {
        loadMessages(activeConversation.id, true)
      }, 3000)
    }
    
    return () => {
      if (messagesIntervalRef.current) {
        clearInterval(messagesIntervalRef.current)
      }
    }
  }, [activeConversation?.id])

  // Fermer le menu d'actions au clic extérieur
  useEffect(() => {
    if (!menuOpenId) return
    const close = () => setMenuOpenId(null)
    document.addEventListener('click', close)
    return () => document.removeEventListener('click', close)
  }, [menuOpenId])

  const handleSelectConversation = async (conv) => {
    setActiveConversation(conv)
    setPartnerTyping(false)
    await loadMessages(conv.id)
  }

  // Signale la frappe (throttlé à 1 ping / 2,5s)
  const handleTyping = () => {
    if (!activeConversation) return
    const now = Date.now()
    if (now - lastTypingSentRef.current < 2500) return
    lastTypingSentRef.current = now
    adminMessageService.sendTyping(activeConversation.id).catch(() => {})
  }

  const handleSendMessage = async (e) => {
    e.preventDefault()
    const userId = getUserId()
    if (!newMessageText.trim() || !activeConversation || !userId) return

    try {
      setSending(true)
      const response = await adminMessageService.sendMessage(
        activeConversation.contact_id,
        newMessageText
      )
      
      if (response.success) {
        // Ajouter le message à la liste
        const newMsg = {
          id: response.data.message_id,
          sender_id: userId,
          message: newMessageText,
          is_mine: true,
          created_at: response.data.created_at || new Date().toISOString(),
          sender_full_name: user?.nom || user?.name || 'Admin'
        }
        
        setMessages([...messages, newMsg])
        setNewMessageText('')
        
        // Mettre à jour la conversation
        const updatedConversations = conversations.map(conv =>
          conv.id === activeConversation.id
            ? { ...conv, last_message: newMessageText, last_message_time: response.data.created_at || new Date().toISOString() }
            : conv
        )
        setConversations(updatedConversations)
        
        // Recharger les conversations pour mettre à jour l'affichage
        loadConversations(true)
        
        setTimeout(scrollToBottom, 100)
      }
    } catch (error) {
      console.error('Erreur envoi message:', error)
      toast.error('Erreur lors de l\'envoi du message')
    } finally {
      setSending(false)
    }
  }

  const startEdit = (msg) => {
    setMenuOpenId(null)
    setEditingId(msg.id)
    setEditText(msg.message || '')
  }

  const cancelEdit = () => {
    setEditingId(null)
    setEditText('')
  }

  const handleSaveEdit = async (msg) => {
    const text = editText.trim()
    if (!text || text === msg.message) { cancelEdit(); return }
    try {
      const response = await adminMessageService.updateMessage(msg.id, text)
      if (response.success) {
        setMessages(prev => prev.map(m => m.id === msg.id ? { ...m, message: text, edited: true } : m))
      }
    } catch (error) {
      console.error('Erreur modification:', error)
      toast.error('Erreur lors de la modification')
    } finally {
      cancelEdit()
    }
  }

  const handleToggleLike = async (msg) => {
    setMessages(prev => prev.map(m => m.id === msg.id ? { ...m, liked: !m.liked } : m))
    try {
      const response = await adminMessageService.toggleLike(msg.id)
      if (response.success) {
        setMessages(prev => prev.map(m => m.id === msg.id ? { ...m, liked: response.liked } : m))
      }
    } catch (error) {
      console.error('Erreur like:', error)
      setMessages(prev => prev.map(m => m.id === msg.id ? { ...m, liked: msg.liked } : m))
    }
  }

  const handleDeleteMessage = async (msg) => {
    setMenuOpenId(null)
    if (!window.confirm('Supprimer ce message ?')) return
    try {
      const response = await adminMessageService.deleteMessage(msg.id)
      if (response.success) {
        setMessages(prev => prev.filter(m => m.id !== msg.id))
      }
    } catch (error) {
      console.error('Erreur suppression:', error)
      toast.error('Erreur lors de la suppression')
    }
  }

  const filteredConversations = conversations.filter(conv =>
    conv.contact_full_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    conv.contact_email?.toLowerCase().includes(searchTerm.toLowerCase())
  )

  const totalUnread = conversations.reduce((sum, conv) => sum + conv.unread_count, 0)

  // Fonction pour formater la date
  const formatTime = (dateString) => {
    if (!dateString) return ''
    const date = new Date(dateString)
    const now = new Date()
    const diff = now - date
    const minutes = Math.floor(diff / 60000)
    const hours = Math.floor(diff / 3600000)
    const days = Math.floor(diff / 86400000)
    
    if (minutes < 1) return 'À l\'instant'
    if (minutes < 60) return `Il y a ${minutes} min`
    if (hours < 24) return `Il y a ${hours}h`
    if (days < 7) return `Il y a ${days}j`
    
    return date.toLocaleDateString('fr-FR', { day: '2-digit', month: 'short' })
  }

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center h-full">
          <div className="text-gray-400 text-lg">Chargement...</div>
        </div>
      </AdminLayout>
    )
  }

  // Fonction pour obtenir les initiales
  const getInitials = (name) => {
    if (!name) return '?'
    const parts = name.split(' ')
    if (parts.length >= 2) {
      return (parts[0].charAt(0) + parts[1].charAt(0)).toUpperCase()
    }
    return name.charAt(0).toUpperCase()
  }

  return (
    <AdminLayout>
      <div className="bg-gray-50 py-6" style={{ height: 'calc(100vh - 80px)', overflow: 'hidden' }}>
        <div className="w-full px-4 flex flex-col h-full">

          <div className="mb-4 flex-shrink-0">
            <h1 className="text-2xl font-bold text-[#001F3F]">Messages</h1>
          </div>

          <div className="flex-1 flex overflow-hidden rounded-2xl shadow-sm min-h-0">

            {/* ── Sidebar ── */}
            <div className={`${activeConversation ? 'hidden md:flex' : 'flex'} w-full md:w-[340px] lg:w-[380px] flex-shrink-0 flex-col bg-white border-r border-gray-200 overflow-hidden`}>
              <div className="px-4 pt-5 pb-3 border-b border-gray-100">
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-2">
                    <span className="font-semibold text-[#001F3F] text-sm">Conversations</span>
                    {totalUnread > 0 && (
                      <span className="bg-[#D4AF37] text-white text-xs font-bold px-2 py-0.5 rounded-full">{totalUnread}</span>
                    )}
                  </div>
                  <button
                    onClick={() => { setShowNewMessageModal(true); loadClientsList() }}
                    className="w-8 h-8 flex items-center justify-center bg-[#D4AF37] text-white rounded-full hover:bg-[#c09d2a] transition-colors"
                    title="Nouvelle conversation"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M12 4v16m8-8H4" />
                    </svg>
                  </button>
                </div>
                <div className="relative">
                  <svg className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                  </svg>
                  <input
                    type="text"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    placeholder="Rechercher..."
                    className="w-full pl-9 pr-4 py-2 bg-gray-100 rounded-full text-sm focus:outline-none focus:ring-2 focus:ring-[#D4AF37]/40"
                  />
                </div>
              </div>

              <div className="flex-1 overflow-y-auto">
                {loading ? (
                  <div className="p-4 space-y-3">
                    {[1,2,3,4].map(i => (
                      <div key={i} className="flex items-center gap-3 animate-pulse">
                        <div className="w-12 h-12 bg-gray-200 rounded-full flex-shrink-0" />
                        <div className="flex-1 space-y-2">
                          <div className="h-3.5 bg-gray-200 rounded-full w-1/2" />
                          <div className="h-3 bg-gray-200 rounded-full w-3/4" />
                        </div>
                      </div>
                    ))}
                  </div>
                ) : filteredConversations.length === 0 ? (
                  <div className="flex flex-col items-center justify-center h-full py-16 text-gray-400">
                    <svg className="w-14 h-14 mb-3 text-gray-200" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                    </svg>
                    <p className="text-sm font-medium text-gray-500 mb-3">Aucune conversation</p>
                    <button
                      onClick={() => { setShowNewMessageModal(true); loadClientsList() }}
                      className="text-xs text-[#D4AF37] hover:underline"
                    >
                      Démarrer une conversation
                    </button>
                  </div>
                ) : (
                  filteredConversations.map((conv) => {
                    const avatarUrl = conv.contact_avatar
                      ? (conv.contact_avatar.startsWith('http') ? conv.contact_avatar : `${conv.contact_avatar}`)
                      : null
                    return (
                      <button
                        key={conv.id}
                        onClick={() => handleSelectConversation(conv)}
                        className={`w-full px-4 py-3 flex items-center gap-3 transition-colors text-left ${
                          activeConversation?.id === conv.id
                            ? 'bg-[#D4AF37]/10 border-r-2 border-[#D4AF37]'
                            : 'hover:bg-gray-50'
                        }`}
                      >
                        <div className="w-12 h-12 flex-shrink-0 relative">
                          {avatarUrl && <img src={avatarUrl} alt={conv.contact_full_name} className="w-full h-full rounded-full object-cover" onError={(e) => { e.target.style.display='none'; e.target.nextSibling.style.display='flex' }} />}
                          <div className="w-full h-full rounded-full bg-gradient-to-br from-[#D4AF37] to-[#b8922a] flex items-center justify-center text-white font-bold text-sm" style={{ display: avatarUrl ? 'none' : 'flex' }}>
                            {getInitials(conv.contact_full_name)}
                          </div>
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center justify-between">
                            <span className={`text-sm truncate ${conv.unread_count > 0 ? 'font-bold text-[#001F3F]' : 'font-medium text-gray-800'}`}>{conv.contact_full_name}</span>
                            <span className="text-[10px] text-gray-400 flex-shrink-0 ml-2">
                              {(() => { const d = new Date(conv.last_message_time); const m = Math.floor((Date.now()-d)/60000); return m < 1 ? 'maintenant' : m < 60 ? `${m}min` : m < 1440 ? `${Math.floor(m/60)}h` : d.toLocaleDateString('fr-FR', {day:'2-digit',month:'short'}) })()}
                            </span>
                          </div>
                          <div className="flex items-center justify-between mt-0.5 gap-2">
                            <div className="flex items-center gap-1.5 flex-1 min-w-0">
                              {conv.last_is_article && (
                                conv.last_image ? (
                                  <img src={conv.last_image} alt="" className="w-8 h-8 rounded object-cover flex-shrink-0 border border-gray-200" onError={(e) => { e.target.style.display='none' }} />
                                ) : (
                                  <div className="w-8 h-8 rounded bg-gray-100 flex items-center justify-center flex-shrink-0 border border-gray-200">
                                    <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                                    </svg>
                                  </div>
                                )
                              )}
                              <p className="text-xs text-gray-500 truncate">{conv.last_message || 'Aucun message'}</p>
                            </div>
                            {conv.unread_count > 0 && (
                              <span className="flex-shrink-0 w-5 h-5 bg-[#D4AF37] text-white text-[10px] font-bold rounded-full flex items-center justify-center">
                                {conv.unread_count > 9 ? '9+' : conv.unread_count}
                              </span>
                            )}
                          </div>
                        </div>
                      </button>
                    )
                  })
                )}
              </div>
            </div>

            {/* ── Zone chat ── */}
            <div className={`${activeConversation ? 'flex' : 'hidden md:flex'} flex-1 flex-col bg-[#f0f2f5] min-w-0`}>
              {activeConversation ? (
                <>
                  {/* Header */}
                  <div className="flex-shrink-0 bg-white border-b border-gray-200 px-4 py-3 flex items-center gap-3 shadow-sm">
                    <button onClick={() => setActiveConversation(null)} className="md:hidden p-1.5 hover:bg-gray-100 rounded-full -ml-1">
                      <svg className="w-5 h-5 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                      </svg>
                    </button>
                    <div className="w-10 h-10 flex-shrink-0 relative">
                      {activeConversation.contact_avatar && <img src={activeConversation.contact_avatar.startsWith('http') ? activeConversation.contact_avatar : activeConversation.contact_avatar} alt="" className="w-full h-full rounded-full object-cover" onError={(e) => { e.target.style.display='none'; e.target.nextSibling.style.display='flex' }} />}
                      <div className="w-full h-full rounded-full bg-gradient-to-br from-[#D4AF37] to-[#b8922a] flex items-center justify-center text-white font-bold text-sm" style={{ display: activeConversation.contact_avatar ? 'none' : 'flex' }}>
                        {getInitials(activeConversation.contact_full_name)}
                      </div>
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-semibold text-[#001F3F] text-sm truncate">{activeConversation.contact_full_name}</p>
                      <p className="text-xs text-gray-400">{activeConversation.contact_phone || activeConversation.contact_email || 'Client'}</p>
                    </div>
                    <button
                      onClick={() => setShowContactDetail(v => !v)}
                      className={`p-2 rounded-full transition-colors flex-shrink-0 ${showContactDetail ? 'bg-[#D4AF37]/20 text-[#D4AF37]' : 'hover:bg-gray-100 text-gray-500'}`}
                      title="Détails du contact"
                    >
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                    </button>
                  </div>

                  {/* Contenu : messages + panneau détail */}
                  <div className="flex-1 flex overflow-hidden min-h-0">

                  {/* Messages */}
                  <div className="flex-1 overflow-y-auto px-4 py-4 space-y-1">
                    {loadingMessages ? (
                      <div className="flex items-center justify-center h-full">
                        <div className="animate-spin w-7 h-7 border-2 border-[#D4AF37] border-t-transparent rounded-full" />
                      </div>
                    ) : messages.length === 0 ? (
                      <div className="flex flex-col items-center justify-center h-full text-center">
                        <div className="w-16 h-16 bg-white rounded-full flex items-center justify-center shadow-md mb-4">
                          <svg className="w-8 h-8 text-[#D4AF37]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                          </svg>
                        </div>
                        <p className="text-sm font-semibold text-gray-600">Démarrez la conversation</p>
                        <p className="text-xs text-gray-400 mt-1">Envoyez un premier message à {activeConversation.contact_full_name}</p>
                      </div>
                    ) : (
                      messages.map((msg, idx) => {
                        const isLast = idx === messages.length - 1 || messages[idx + 1]?.is_mine !== msg.is_mine
                        const time = msg.created_at ? new Date(msg.created_at).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' }) : ''
                        return (
                          <div key={msg.id} className={`flex items-end gap-2 mb-1 ${msg.is_mine ? 'flex-row-reverse' : 'flex-row'}`}>
                            <div className="w-8 flex-shrink-0 mb-1">
                              {!msg.is_mine && isLast && (
                                <div className="w-8 h-8 rounded-full bg-gradient-to-br from-[#D4AF37] to-[#b8922a] flex items-center justify-center text-white font-bold text-xs">
                                  {getInitials(activeConversation.contact_full_name)}
                                </div>
                              )}
                            </div>
                            <div className={`group relative max-w-[70%] flex flex-col ${msg.is_mine ? 'items-end' : 'items-start'}`}>
                              {msg.urlArticle ? (
                                <div className={`w-60 rounded-2xl overflow-hidden cursor-pointer hover:brightness-95 transition-all shadow-sm ${msg.is_mine ? 'rounded-tr-sm' : 'rounded-tl-sm'}`}>
                                  {msg.imageArticle ? (
                                    <img src={msg.imageArticle} alt={msg.nomArticle} className="w-full h-40 object-cover" />
                                  ) : (
                                    <div className="w-full h-28 bg-gray-200 flex items-center justify-center">
                                      <svg className="w-10 h-10 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                                      </svg>
                                    </div>
                                  )}
                                  <div className={`px-3 py-2.5 ${msg.is_mine ? 'bg-[#D4AF37]' : 'bg-white'}`}>
                                    <p className={`text-sm font-semibold truncate ${msg.is_mine ? 'text-white' : 'text-[#001F3F]'}`}>{msg.nomArticle || 'Voir l\'annonce'}</p>
                                    <p className={`text-xs mt-0.5 ${msg.is_mine ? 'text-white/70' : 'text-[#D4AF37]'}`}>Voir l'annonce →</p>
                                  </div>
                                </div>
                              ) : editingId === msg.id ? (
                                /* ── Mode édition ── */
                                <div className="flex flex-col gap-1.5 w-full min-w-[200px]">
                                  <textarea
                                    autoFocus
                                    value={editText}
                                    onChange={(e) => setEditText(e.target.value)}
                                    onKeyDown={(e) => {
                                      if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleSaveEdit(msg) }
                                      if (e.key === 'Escape') cancelEdit()
                                    }}
                                    rows={2}
                                    className="w-full px-3 py-2 text-sm rounded-xl border border-[#D4AF37] focus:outline-none focus:ring-2 focus:ring-[#D4AF37]/30 resize-none text-gray-800"
                                  />
                                  <div className="flex items-center gap-2 justify-end">
                                    <button onClick={cancelEdit} className="text-xs text-gray-500 hover:text-gray-700 px-2 py-1">Annuler</button>
                                    <button onClick={() => handleSaveEdit(msg)} className="text-xs font-semibold text-white bg-[#D4AF37] hover:bg-[#c09d2a] px-3 py-1 rounded-lg">Enregistrer</button>
                                  </div>
                                </div>
                              ) : (
                                <div className="flex items-center gap-1.5">
                                  {/* Actions au survol */}
                                  <div className={`flex items-center gap-0.5 opacity-0 group-hover:opacity-100 transition-opacity ${msg.is_mine ? 'order-first' : 'order-last'}`}>
                                    <button
                                      onClick={(e) => { e.stopPropagation(); handleToggleLike(msg) }}
                                      className={`p-1 rounded-full hover:bg-gray-200 ${msg.liked ? 'text-red-500' : 'text-gray-400'}`}
                                      title="J'aime"
                                    >
                                      <svg className="w-4 h-4" fill={msg.liked ? 'currentColor' : 'none'} stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
                                      </svg>
                                    </button>
                                    {(msg.is_mine || canModerate) && (
                                      <div className="relative">
                                        <button
                                          onClick={(e) => { e.stopPropagation(); setMenuOpenId(menuOpenId === msg.id ? null : msg.id) }}
                                          className="p-1 rounded-full hover:bg-gray-200 text-gray-500"
                                        >
                                          <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                                            <path d="M12 8a2 2 0 100-4 2 2 0 000 4zm0 6a2 2 0 100-4 2 2 0 000 4zm0 6a2 2 0 100-4 2 2 0 000 4z" />
                                          </svg>
                                        </button>
                                        {menuOpenId === msg.id && (
                                          <div className="absolute right-0 bottom-full mb-1 w-32 bg-white rounded-lg shadow-lg border border-gray-200 py-1 z-20">
                                            <button onClick={() => startEdit(msg)} className="w-full px-3 py-1.5 text-left text-sm text-gray-700 hover:bg-gray-50 flex items-center gap-2">
                                              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" /></svg>
                                              Modifier
                                            </button>
                                            <button onClick={() => handleDeleteMessage(msg)} className="w-full px-3 py-1.5 text-left text-sm text-red-600 hover:bg-red-50 flex items-center gap-2">
                                              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" /></svg>
                                              Supprimer
                                            </button>
                                          </div>
                                        )}
                                      </div>
                                    )}
                                  </div>
                                  {/* Bulle (double-clic = j'aime) */}
                                  <div
                                    onDoubleClick={() => handleToggleLike(msg)}
                                    className={`relative px-3.5 py-2 shadow-sm max-w-full select-none ${msg.is_mine ? 'bg-[#D4AF37] text-white rounded-2xl rounded-tr-sm' : 'bg-white text-gray-800 rounded-2xl rounded-tl-sm'}`}
                                  >
                                    <p className="text-sm leading-relaxed break-words whitespace-pre-wrap">{msg.message}</p>
                                    {msg.liked && (
                                      <span className={`absolute -bottom-2 ${msg.is_mine ? '-left-1' : '-right-1'} w-5 h-5 bg-white rounded-full shadow flex items-center justify-center border border-gray-100`}>
                                        <svg className="w-3 h-3 text-red-500" fill="currentColor" viewBox="0 0 24 24">
                                          <path d="M12 21.35l-1.45-1.32C5.4 15.36 2 12.28 2 8.5 2 5.42 4.42 3 7.5 3c1.74 0 3.41.81 4.5 2.09C13.09 3.81 14.76 3 16.5 3 19.58 3 22 5.42 22 8.5c0 3.78-3.4 6.86-8.55 11.54L12 21.35z" />
                                        </svg>
                                      </span>
                                    )}
                                  </div>
                                </div>
                              )}
                              {editingId !== msg.id && (
                                <span className="text-[10px] text-gray-400 mt-1 px-1">
                                  {time}
                                  {msg.edited && <span className="italic"> · modifié</span>}
                                </span>
                              )}
                            </div>
                          </div>
                        )
                      })
                    )}
                    {/* Indicateur "en train d'écrire" */}
                    {partnerTyping && (
                      <div className="flex items-end gap-2 mb-1">
                        <div className="w-8 flex-shrink-0 mb-1">
                          <div className="w-8 h-8 rounded-full bg-gradient-to-br from-[#D4AF37] to-[#b8922a] flex items-center justify-center text-white font-bold text-xs">
                            {getInitials(activeConversation.contact_full_name)}
                          </div>
                        </div>
                        <div className="bg-white rounded-2xl rounded-tl-sm px-4 py-3 shadow-sm flex items-center gap-1">
                          <span className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
                          <span className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
                          <span className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
                        </div>
                      </div>
                    )}
                    <div ref={messagesEndRef} />
                  </div>

                  {/* Panneau détail contact */}
                  {showContactDetail && (
                    <div className="w-72 flex-shrink-0 bg-white border-l border-gray-200 overflow-y-auto flex flex-col">
                      <div className="p-4 border-b border-gray-100 flex items-center justify-between">
                        <span className="font-semibold text-sm text-[#001F3F]">Détails</span>
                        <button onClick={() => setShowContactDetail(false)} className="p-1 hover:bg-gray-100 rounded-full transition-colors">
                          <svg className="w-4 h-4 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                          </svg>
                        </button>
                      </div>
                      <div className="flex flex-col items-center pt-6 pb-4 px-4 border-b border-gray-100">
                        <div className="w-16 h-16 relative">
                          {activeConversation.contact_avatar && <img src={activeConversation.contact_avatar} alt="" className="w-full h-full rounded-full object-cover" onError={(e) => { e.target.style.display='none'; e.target.nextSibling.style.display='flex' }} />}
                          <div className="w-full h-full rounded-full bg-gradient-to-br from-[#D4AF37] to-[#b8922a] flex items-center justify-center text-white font-bold text-lg" style={{ display: activeConversation.contact_avatar ? 'none' : 'flex' }}>
                            {getInitials(activeConversation.contact_full_name)}
                          </div>
                        </div>
                        <p className="mt-3 font-bold text-[#001F3F] text-base text-center">{activeConversation.contact_full_name}</p>
                        <p className="text-xs text-gray-500 mt-0.5">Client</p>
                      </div>
                      <div className="p-4 space-y-3">
                        {activeConversation.contact_phone && (
                          <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-xl">
                            <div className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center flex-shrink-0">
                              <svg className="w-4 h-4 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                              </svg>
                            </div>
                            <div>
                              <p className="text-[10px] text-gray-400 uppercase tracking-wide">Téléphone</p>
                              <p className="text-sm font-medium text-gray-800">{activeConversation.contact_phone}</p>
                            </div>
                          </div>
                        )}
                        {activeConversation.contact_email && (
                          <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-xl">
                            <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0">
                              <svg className="w-4 h-4 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                              </svg>
                            </div>
                            <div>
                              <p className="text-[10px] text-gray-400 uppercase tracking-wide">Email</p>
                              <p className="text-sm font-medium text-gray-800 break-all">{activeConversation.contact_email}</p>
                            </div>
                          </div>
                        )}
                        <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-xl">
                          <div className="w-8 h-8 bg-[#D4AF37]/20 rounded-full flex items-center justify-center flex-shrink-0">
                            <svg className="w-4 h-4 text-[#D4AF37]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                            </svg>
                          </div>
                          <div>
                            <p className="text-[10px] text-gray-400 uppercase tracking-wide">Messages</p>
                            <p className="text-sm font-medium text-gray-800">{messages.length} message{messages.length > 1 ? 's' : ''}</p>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}

                  </div>{/* fin flex contenu */}

                  {/* Input */}
                  <div className="flex-shrink-0 bg-white border-t border-gray-100 px-4 py-3">
                    <form onSubmit={handleSendMessage} className="flex items-center gap-2">
                      <input
                        type="text"
                        value={newMessageText}
                        onChange={(e) => { setNewMessageText(e.target.value); handleTyping() }}
                        placeholder="Écrivez un message..."
                        disabled={sending}
                        className="flex-1 px-4 py-2.5 bg-gray-100 rounded-full text-sm focus:outline-none focus:ring-2 focus:ring-[#D4AF37]/40 disabled:opacity-50"
                      />
                      <button
                        type="submit"
                        disabled={!newMessageText.trim() || sending}
                        className="w-10 h-10 bg-[#D4AF37] text-white rounded-full flex items-center justify-center hover:bg-[#c09d2a] transition-colors disabled:opacity-40 disabled:cursor-not-allowed flex-shrink-0"
                      >
                        {sending ? (
                          <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                        ) : (
                          <svg className="w-4 h-4 translate-x-0.5" fill="currentColor" viewBox="0 0 24 24">
                            <path d="M2.01 21L23 12 2.01 3 2 10l15 2-15 2z" />
                          </svg>
                        )}
                      </button>
                    </form>
                  </div>
                </>
              ) : (
                <div className="flex flex-col items-center justify-center h-full text-center">
                  <div className="w-24 h-24 bg-white rounded-full flex items-center justify-center shadow-md mb-6">
                    <svg className="w-12 h-12 text-[#D4AF37]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                    </svg>
                  </div>
                  <h2 className="text-xl font-bold text-gray-700 mb-2">Messages Admin</h2>
                  <p className="text-sm text-gray-400 max-w-xs mb-4">Sélectionnez une conversation ou démarrez-en une nouvelle</p>
                  <button
                    onClick={() => { setShowNewMessageModal(true); loadClientsList() }}
                    className="px-4 py-2 bg-[#D4AF37] text-white rounded-lg hover:bg-[#c09d2a] transition-colors text-sm font-medium"
                  >
                    Nouvelle conversation
                  </button>
                </div>
              )}
            </div>

          </div>
        </div>
      </div>

      {/* Modal Nouveau Message */}
      {showNewMessageModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-xl max-w-2xl w-full max-h-[90vh] flex flex-col">
            {/* Header modal */}
            <div className="px-6 py-4 border-b border-gray-100 flex items-center justify-between flex-shrink-0">
              <div>
                <h2 className="text-lg font-bold text-[#001F3F]">
                  {selectedClient ? `À : ${selectedClient.name}` : 'Nouveau message'}
                </h2>
                {postContext && <p className="text-xs text-gray-500 mt-0.5">Concernant : {postContext.title}</p>}
              </div>
              <button
                onClick={() => { setShowNewMessageModal(false); setPostContext(null); setSelectedClient(null); setNewMessageText(''); setSearchClientQuery('') }}
                className="p-2 hover:bg-gray-100 rounded-full transition-colors"
              >
                <svg className="w-5 h-5 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            {/* Body */}
            <div className="p-5 space-y-4 overflow-y-auto flex-1">
              {/* Contexte article */}
              {postContext && (
                <div className="flex items-center gap-3 p-3 bg-amber-50 border border-amber-200 rounded-xl">
                  {postContext.images?.[0] && (
                    <img src={postContext.images[0]} alt="" className="w-12 h-12 rounded-lg object-cover flex-shrink-0" onError={(e) => e.target.style.display='none'} />
                  )}
                  <div className="flex-1 min-w-0">
                    <p className="text-xs font-semibold text-amber-700 uppercase tracking-wide">{postContext.type}</p>
                    <p className="text-sm font-bold text-gray-800 truncate">{postContext.title}</p>
                    {postContext.price && <p className="text-xs text-green-600 font-medium">{parseInt(postContext.price).toLocaleString()} FCFA</p>}
                  </div>
                </div>
              )}

              {/* Formulaire de message */}
              <div className="space-y-4">
                {!selectedClient ? (
                  <div>
                    <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Destinataire</label>
                    <div className="relative">
                      <div className="relative">
                        <svg className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                        </svg>
                        <input
                          type="text"
                          value={searchClientQuery}
                          onChange={(e) => {
                            setSearchClientQuery(e.target.value)
                            loadClientsList(e.target.value)
                          }}
                          placeholder="Nom, email ou téléphone..."
                          className="w-full pl-9 pr-4 py-2.5 border border-gray-300 rounded-xl focus:ring-2 focus:ring-[#D4AF37]/40 focus:border-[#D4AF37] transition-all text-sm bg-gray-50"
                        />
                      </div>
                      {searchClientQuery && (
                        <div className="absolute top-full left-0 right-0 mt-2 bg-white border-2 border-gray-200 rounded-xl shadow-2xl max-h-80 overflow-y-auto z-10">
                          {loadingClients ? (
                            <div className="flex flex-col items-center justify-center py-12">
                              <div className="animate-spin rounded-full h-10 w-10 border-4 border-[#D4AF37] border-t-transparent mb-3"></div>
                              <p className="text-sm text-gray-500">Recherche en cours...</p>
                            </div>
                          ) : clientsList.length === 0 ? (
                            <div className="text-center py-12">
                              <svg className="w-16 h-16 text-gray-300 mx-auto mb-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.172 16.172a4 4 0 015.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                              </svg>
                              <p className="text-gray-500 font-medium">Aucun utilisateur trouvé</p>
                              <p className="text-sm text-gray-400 mt-1">Essayez un autre terme de recherche</p>
                            </div>
                          ) : (
                            <div className="divide-y divide-gray-100">
                              {clientsList.map((client, index) => {
                                // Déterminer la couleur du badge selon le rôle
                                const getRoleBadgeColor = (roleId) => {
                                  switch(roleId) {
                                    case 1: return 'bg-gradient-to-r from-purple-600 to-purple-700 text-white'; // Super Admin
                                    case 2: return 'bg-gradient-to-r from-blue-600 to-blue-700 text-white'; // Admin
                                    case 3: return 'bg-gradient-to-r from-green-600 to-green-700 text-white'; // Superviseur
                                    case 4: return 'bg-gradient-to-r from-orange-600 to-orange-700 text-white'; // Animateur
                                    case 6: return 'bg-gradient-to-r from-yellow-600 to-yellow-700 text-white'; // Staff
                                    default: return 'bg-gradient-to-r from-gray-500 to-gray-600 text-white'; // Client
                                  }
                                };
                                
                                return (
                                  <button
                                    key={client.id}
                                    type="button"
                                    onClick={() => {
                                      setSelectedClient(client)
                                      setSearchClientQuery('')
                                    }}
                                    className={`w-full p-4 flex items-center gap-4 hover:bg-gradient-to-r hover:from-yellow-50 hover:to-orange-50 transition-all duration-200 ${
                                      index === 0 ? 'rounded-t-xl' : ''
                                    } ${index === clientsList.length - 1 ? 'rounded-b-xl' : ''}`}
                                  >
                                    <div className="relative">
                                      {client.avatar ? (
                                        <img 
                                          src={client.avatar.startsWith('http') ? client.avatar : `http://localhost/ligdi/public/${client.avatar}`}
                                          alt={client.name}
                                          className="w-14 h-14 rounded-full object-cover shadow-md border-2 border-white"
                                          onError={(e) => {
                                            e.target.style.display = 'none'
                                            e.target.nextSibling.style.display = 'flex'
                                          }}
                                        />
                                      ) : null}
                                      <div 
                                        className="w-14 h-14 rounded-full bg-gradient-to-br from-[#D4AF37] to-[#c09d2a] flex items-center justify-center text-white font-bold text-lg shadow-md border-2 border-white"
                                        style={{ display: client.avatar ? 'none' : 'flex' }}
                                      >
                                        {getInitials(client.name)}
                                      </div>
                                      {/* Badge rôle sur l'avatar */}
                                      <div className={`absolute -bottom-1 -right-1 px-2 py-0.5 rounded-full text-[9px] font-bold shadow-lg ${getRoleBadgeColor(client.role_id)}`}>
                                        {client.role_name === 'Super Admin' ? 'SA' : 
                                         client.role_name === 'Admin' ? 'AD' :
                                         client.role_name === 'Superviseur' ? 'SU' :
                                         client.role_name === 'Animateur' ? 'AN' :
                                         client.role_name === 'Staff' ? 'ST' : 'CL'}
                                      </div>
                                    </div>
                                    <div className="flex-1 text-left">
                                      <div className="flex items-center gap-2 mb-1">
                                        <h3 className="font-bold text-gray-900 text-base">{client.name}</h3>
                                        <span className={`px-2.5 py-0.5 rounded-full text-[10px] font-bold shadow-sm ${getRoleBadgeColor(client.role_id)}`}>
                                          {client.role_name}
                                        </span>
                                      </div>
                                      <div className="flex items-center gap-3 text-xs text-gray-600">
                                        {client.email && (
                                          <span className="flex items-center gap-1">
                                            <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                                            </svg>
                                            {client.email}
                                          </span>
                                        )}
                                        {client.phone && (
                                          <span className="flex items-center gap-1">
                                            <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                                            </svg>
                                            {client.phone}
                                          </span>
                                        )}
                                      </div>
                                    </div>
                                    <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                                    </svg>
                                  </button>
                                );
                              })}
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                ) : (
                  <div>
                    <label className="flex text-sm font-bold text-gray-800 mb-3 items-center gap-2">
                      <svg className="w-5 h-5 text-[#D4AF37]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                      </svg>
                      Destinataire sélectionné
                    </label>
                    <div className="bg-gradient-to-r from-yellow-50 via-orange-50 to-yellow-50 rounded-xl p-5 border-2 border-yellow-300 shadow-lg flex items-center justify-between">
                      <div className="flex items-center gap-4">
                        <div className="relative">
                          {selectedClient.avatar ? (
                            <img 
                              src={selectedClient.avatar.startsWith('http') ? selectedClient.avatar : `http://localhost/ligdi/public/${selectedClient.avatar}`}
                              alt={selectedClient.name}
                              className="w-16 h-16 rounded-full object-cover shadow-lg border-3 border-white"
                              onError={(e) => {
                                e.target.style.display = 'none'
                                e.target.nextSibling.style.display = 'flex'
                              }}
                            />
                          ) : null}
                          <div 
                            className="w-16 h-16 rounded-full bg-gradient-to-br from-[#D4AF37] to-[#c09d2a] flex items-center justify-center text-white font-bold text-xl shadow-lg border-3 border-white"
                            style={{ display: selectedClient.avatar ? 'none' : 'flex' }}
                          >
                            {getInitials(selectedClient.name)}
                          </div>
                          {/* Badge rôle */}
                          {selectedClient.role_name && (
                            <div className={`absolute -bottom-1 -right-1 px-2.5 py-1 rounded-full text-[10px] font-bold shadow-lg ${
                              selectedClient.role_id === 1 ? 'bg-gradient-to-r from-purple-600 to-purple-700 text-white' :
                              selectedClient.role_id === 2 ? 'bg-gradient-to-r from-blue-600 to-blue-700 text-white' :
                              selectedClient.role_id === 3 ? 'bg-gradient-to-r from-green-600 to-green-700 text-white' :
                              selectedClient.role_id === 4 ? 'bg-gradient-to-r from-orange-600 to-orange-700 text-white' :
                              selectedClient.role_id === 6 ? 'bg-gradient-to-r from-yellow-600 to-yellow-700 text-white' :
                              'bg-gradient-to-r from-gray-500 to-gray-600 text-white'
                            }`}>
                              {selectedClient.role_name === 'Super Admin' ? 'SA' : 
                               selectedClient.role_name === 'Admin' ? 'AD' :
                               selectedClient.role_name === 'Superviseur' ? 'SU' :
                               selectedClient.role_name === 'Animateur' ? 'AN' :
                               selectedClient.role_name === 'Staff' ? 'ST' : 'CL'}
                            </div>
                          )}
                        </div>
                        <div>
                          <div className="flex items-center gap-2 mb-1">
                            <p className="font-bold text-gray-900 text-lg">{selectedClient.name}</p>
                            {selectedClient.role_name && (
                              <span className={`px-3 py-1 rounded-full text-xs font-bold shadow-md ${
                                selectedClient.role_id === 1 ? 'bg-gradient-to-r from-purple-600 to-purple-700 text-white' :
                                selectedClient.role_id === 2 ? 'bg-gradient-to-r from-blue-600 to-blue-700 text-white' :
                                selectedClient.role_id === 3 ? 'bg-gradient-to-r from-green-600 to-green-700 text-white' :
                                selectedClient.role_id === 4 ? 'bg-gradient-to-r from-orange-600 to-orange-700 text-white' :
                                selectedClient.role_id === 6 ? 'bg-gradient-to-r from-yellow-600 to-yellow-700 text-white' :
                                'bg-gradient-to-r from-gray-500 to-gray-600 text-white'
                              }`}>
                                {selectedClient.role_name}
                              </span>
                            )}
                          </div>
                          <div className="flex items-center gap-4 text-sm text-gray-700">
                            {selectedClient.email && (
                              <span className="flex items-center gap-1.5">
                                <svg className="w-4 h-4 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                                </svg>
                                {selectedClient.email}
                              </span>
                            )}
                            {selectedClient.phone && (
                              <span className="flex items-center gap-1.5">
                                <svg className="w-4 h-4 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                                </svg>
                                {selectedClient.phone}
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                      <button
                        type="button"
                        onClick={() => {
                          setSelectedClient(null)
                          setSearchClientQuery('')
                        }}
                        className="p-3 hover:bg-red-100 rounded-xl transition-all duration-200 group"
                        title="Changer de destinataire"
                      >
                        <svg className="w-6 h-6 text-red-600 group-hover:text-red-700" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                      </button>
                    </div>
                  </div>
                )}

                <div>
                  <label className="flex text-sm font-bold text-gray-800 mb-3 items-center gap-2">
                    <svg className="w-5 h-5 text-[#D4AF37]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 8h10M7 12h4m1 8l-4-4H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-3l-4 4z" />
                    </svg>
                    Sujet du message (optionnel)
                  </label>
                  <input
                    type="text"
                    placeholder="Ex: Concernant votre publication..."
                    defaultValue={postContext ? `Concernant votre publication "${postContext.title}"` : ''}
                    className="w-full px-4 py-3.5 border-2 border-gray-300 rounded-xl focus:ring-2 focus:ring-[#D4AF37] focus:border-[#D4AF37] transition-all shadow-sm text-base"
                  />
                </div>

                <div>
                  <label className="flex text-sm font-bold text-gray-800 mb-3 items-center gap-2">
                    <svg className="w-5 h-5 text-[#D4AF37]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                    </svg>
                    Votre message
                    <span className="text-red-500">*</span>
                  </label>
                  <textarea
                    value={newMessageText}
                    onChange={(e) => setNewMessageText(e.target.value)}
                    placeholder="Rédigez votre message ici..."
                    className="w-full px-4 py-3.5 border-2 border-gray-300 rounded-xl focus:ring-2 focus:ring-[#D4AF37] focus:border-[#D4AF37] transition-all resize-none shadow-sm text-base"
                    rows="8"
                  />
                  <div className="flex items-center justify-between mt-2">
                    <p className="text-xs text-gray-500">
                      {newMessageText.length} caractères
                    </p>
                    {newMessageText.trim() && (
                      <p className="text-xs text-green-600 font-medium flex items-center gap-1">
                        <svg className="w-3.5 h-3.5" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                        </svg>
                        Message prêt
                      </p>
                    )}
                  </div>
                </div>
              </div>
            </div>

            {/* Footer */}
            <div className="px-8 py-5 border-t-2 border-gray-200 bg-gradient-to-r from-gray-50 to-yellow-50 rounded-b-2xl flex justify-between items-center gap-3 flex-shrink-0">
              <div className="text-sm text-gray-600">
                {selectedClient ? (
                  <span className="flex items-center gap-1.5">
                    <svg className="w-4 h-4 text-green-600" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                    </svg>
                    Destinataire sélectionné
                  </span>
                ) : (
                  <span className="flex items-center gap-1.5">
                    <svg className="w-4 h-4 text-orange-500" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                    </svg>
                    Sélectionnez un destinataire
                  </span>
                )}
              </div>
              <div className="flex gap-3">
                <button
                  onClick={() => {
                    setShowNewMessageModal(false)
                    setPostContext(null)
                    setSelectedClient(null)
                    setNewMessageText('')
                    setSearchClientQuery('')
                  }}
                  className="px-6 py-3 border-2 border-gray-400 text-gray-700 rounded-xl hover:bg-white hover:border-gray-500 transition-all duration-200 font-semibold shadow-sm"
                >
                  Annuler
                </button>
                <button
                  onClick={() => {
                    if (!selectedClient) {
                      toast.error('⚠️ Veuillez sélectionner un destinataire')
                      return
                    }
                    if (!newMessageText.trim()) {
                      toast.error('⚠️ Veuillez saisir un message')
                      return
                    }
                    createOrOpenConversation(selectedClient.id, selectedClient)
                    toast.success('✉️ Message envoyé avec succès!')
                    setShowNewMessageModal(false)
                    setPostContext(null)
                    setSelectedClient(null)
                    setNewMessageText('')
                    setSearchClientQuery('')
                  }}
                  disabled={!selectedClient || !newMessageText.trim()}
                  className="px-6 py-3 bg-gradient-to-r from-[#D4AF37] via-[#c09d2a] to-[#D4AF37] text-white rounded-xl hover:shadow-xl transition-all duration-200 font-bold flex items-center justify-center gap-2 disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:shadow-none bg-[length:200%] hover:bg-right-bottom"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" />
                  </svg>
                  Envoyer le message
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </AdminLayout>
  )
}

export default AdminMessagesPage
